import React, { lazy, Suspense } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { NotificationProvider } from './context/NotificationContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import CallBack from './pages/auth/CallBack.jsx';
import { store } from './redux/store';
import { HelmetProvider } from 'react-helmet-async';
import AuthRoutes from './routes/AuthRoutes.jsx';
import UserRoutes from './routes/UserRoutes.jsx';
import AdminRoutes from './routes/AdminRoutes.jsx';
import './App.css';
import Loader from './components/common/Loader.jsx';

const PublicViewPlans   = lazy(() => import('./website/PublicViewPlans.jsx'));
const HomePage          = lazy(() => import('./website/HomePage/HomePage.jsx'));
const PrivacyPolicy     = lazy(() => import('./website/PrivacyPolicy.jsx'));
const TermsOfService    = lazy(() => import('./website/TermsOfService.jsx'));
const AboutUs           = lazy(() => import('./website/AboutUs.jsx'));
const ContactUs         = lazy(() => import('./website/ContactUs.jsx'));
const Blogs             = lazy(() => import('./website/Blogs.jsx'));
const BlogDetail        = lazy(() => import('./website/BlogDetail.jsx'));
const PublicCourseDetails = lazy(() => import('./website/CourseDetails.jsx'));
const PublicLiveSessionDetails = lazy(() => import('./website/LiveSessionDetails.jsx'));
const CoursesSearch            = lazy(() => import('./website/CoursesSearch.jsx'));
const LiveSessions             = lazy(() => import('./website/LiveSessions.jsx'));

function AppRouter() {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* Always-public routes — never redirect these */}
        <Route path="/" element={isAuthenticated ? (user?.role?.toLowerCase() === 'trainee' ? <Navigate to="/user/dashboard" replace /> : <Navigate to="/admin/dashboard" replace />) : <HomePage />} />
        <Route path="/auth/callback" element={<CallBack />} />
        <Route path="/plans"           element={<PublicViewPlans />} />
        <Route path="/privacy-policy"   element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/about-us"         element={<AboutUs />} />
        <Route path="/contact-us"       element={<ContactUs />} />
        <Route path="/blogs"            element={<Blogs />} />
        <Route path="/blogs/:slug"       element={<BlogDetail />} />
        <Route path="/courses/explore"     element={<CoursesSearch />} />
        <Route path="/live-sessions"        element={<LiveSessions />} />
        <Route path="/courses/:encodedId" element={<PublicCourseDetails />} />
        <Route path="/live-sessions/:encodedId" element={<PublicLiveSessionDetails />} />

        {/* Auth routes */}
        {!isAuthenticated && <Route path="/auth/*" element={<AuthRoutes />} />}

        {/* User routes */}
        {isAuthenticated && user?.role?.toLowerCase() === 'trainee' && <Route path="/user/*" element={<UserRoutes />} />}

        {/* Admin routes */}
        {isAuthenticated && user?.role?.toLowerCase() !== 'trainee' && <Route path="/admin/*" element={<AdminRoutes />} />}

        {/* Fallback — only redirect if truly authenticated, otherwise go home */}
        <Route path="*" element={
          isAuthenticated
            ? (user?.role?.toLowerCase() === 'trainee' ? <Navigate to="/user/dashboard" replace /> : <Navigate to="/admin/dashboard" replace />)
            : <Navigate to="/" replace />
        } />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="font-sans">
      <HelmetProvider>
      <Provider store={store}>
        <PermissionsProvider>
          <NotificationProvider>
            <AuthProvider>
              <BrowserRouter>
                <AppRouter />
              </BrowserRouter>
            </AuthProvider>
          </NotificationProvider>
        </PermissionsProvider>
      </Provider>
      </HelmetProvider>
    </div>
  );
}

export default App;
